/**
 * 회사 메뉴 · 부서별 메뉴 권한 API (2026-08-25).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/admin/menu-access-admin.routes.ts`.
 * 판정은 서버 한 곳(`lib/menu-access.ts`)에만 있고, 이 화면은 **무엇을 켰나**만 주고받는다.
 *
 * 두 층이다:
 *   · 회사 층(`company`)  — 우리 회사가 쓰는 메뉴. 여기서 끄면 부서 규칙으로도 안 되살아난다.
 *   · 대상 층(`rules`)    — 부서·팀·역할·개인의 허용 목록. **규칙이 하나도 없으면 전원이 본다.**
 */
import { apiClient } from './client';

/**
 * 대상은 셋이다 (2026-08-28 · 자리 재정의 판 4).
 *
 * ★★ 개인별(`user`)과 역할(`role`)은 뺐다 — 역할의 뜻은 「통합설정을 할 수 있는가」 하나이고,
 *    개인별은 「이 사람은 왜 보이지」를 명부에서 한 명씩 뒤지게 만든다.
 *    「부서·팀 없는 직원」이 그 자리를 대신한다(배정 전 계정에도 규칙을 줄 수 있다).
 */
export type MenuScopeType = 'division' | 'team' | 'unassigned' | 'user';
// ★ 2026-09-15 R1+R7 — 개인(`user`)이 다시 대상이다. 단 **칸 단위로만** 적는다(전량 PUT 에 user = 400).
//   `unassigned` 는 새 규칙을 만들지 않는다(판정에 안 씀) — 남은 줄은 지우거나 migrate 한다.

/** 「부서·팀 없는 직원」의 고정 id — 사람마다가 아니라 한 무리라서 하나뿐이다. */
export const UNASSIGNED_SCOPE_ID = 'all';

export interface MenuCatalogItem {
  menuKey: string;
  label: string;
  icon: string;
  section: string;
  sortOrder: number;
  moduleKey: string | null;
  /** 이 회사에서 그 메뉴의 계약 모듈이 켜져 있나 (R1 · 2026-09-15). 옛 서버는 안 준다 → 켬으로 본다. */
  moduleEnabled?: boolean;
}

export interface CompanyMenuSetting {
  menuKey: string;
  enabled: boolean;
  sortOrder: number | null;
}

export interface MenuAccessRule {
  menuKey: string;
  scopeType: MenuScopeType;
  scopeId: string;
  allow: boolean;
}

export interface MenuAccessState {
  /** 관리 콘솔의 메뉴 카탈로그를 못 읽은 경우 false — 고를 것이 없다. */
  catalogPresent: boolean;
  /** 운영자가 내려 준 메뉴만 들어 있다(끈 것은 애초에 안 온다). */
  menus: MenuCatalogItem[];
  company: CompanyMenuSetting[];
  rules: MenuAccessRule[];
  /**
   * 사람별 판정 팀 `userId → teamId` (R1 · 2026-09-15) — 서버가 «먼저 들어간 활성 팀 하나»로 고른 값.
   * 두 팀 소속자의 「부서 기준」을 서버와 같게 그리려면 이것이 있어야 한다. 없는 사람 = 팀 없음.
   */
  viewerTeams: Record<string, string>;
}

export async function getMenuAccess(): Promise<MenuAccessState> {
  const { data } = await apiClient.get<MenuAccessState>('/admin/menu-access');
  const vt = data?.viewerTeams;
  return {
    catalogPresent: data?.catalogPresent ?? false,
    menus: Array.isArray(data?.menus) ? data.menus : [],
    company: Array.isArray(data?.company) ? data.company : [],
    rules: Array.isArray(data?.rules) ? data.rules : [],
    viewerTeams: vt && typeof vt === 'object' && !Array.isArray(vt) ? vt : {},
  };
}

/** 회사가 쓰는 메뉴 — 보낸 항목만 갱신한다(안 보낸 메뉴는 그대로). */
export async function saveCompanyMenus(
  items: Array<{ menuKey: string; enabled: boolean; sortOrder?: number | null }>,
): Promise<void> {
  await apiClient.put('/admin/menu-access/company', { items });
}

/**
 * 한 대상의 허용 목록 — **전량 교체**다. 지금 켜진 것 전부를 보낸다.
 * ⚠ 빈 배열은 "규칙 삭제" 가 아니라 **아무것도 못 본다**는 뜻이다. 되돌리려면 clearMenuRules.
 */
export async function saveMenuRules(
  scopeType: MenuScopeType, scopeId: string, menuKeys: string[],
): Promise<void> {
  await apiClient.put('/admin/menu-access/rules', { scopeType, scopeId, menuKeys });
}

/** 되돌리기 — 이 대상의 규칙을 없애 위 층을 물려받게 한다. */
export async function clearMenuRules(scopeType: MenuScopeType, scopeId: string): Promise<void> {
  await apiClient.delete(`/admin/menu-access/rules/${scopeType}/${encodeURIComponent(scopeId)}`);
}

/* ─────────── 칸 단위 (R2 · 2026-09-15 · 서버 PR #1370) ─────────── */

/** 칸 저장이 받는 대상 — `unassigned` 는 새 규칙을 못 만든다(400 SCOPE_INVALID). */
export type MenuCellScope = 'division' | 'team' | 'user';

export interface MenuCellResult {
  ok: boolean;
  scopeType: string;
  scopeId: string;
  menuKey: string;
  /** PUT 에만 있다. */
  allow?: boolean;
  /** 바꾸기 전 값 — 없던 칸이면 null(= 상속). */
  previous: boolean | null;
  /** DELETE 에만 있다 — 지운 줄 수. */
  cleared?: number;
}

const cellPath = (scopeType: string, scopeId: string, menuKey: string): string =>
  `/admin/menu-access/rules/${scopeType}/${encodeURIComponent(scopeId)}/${encodeURIComponent(menuKey)}`;

/**
 * 칸 하나 — 허용/차단. ⚠ 「상속」은 여기로 보내지 않는다(`{allow:null}` 은 400 ALLOW_INVALID) → `clearMenuCell`.
 */
export async function saveMenuCell(
  scopeType: MenuCellScope, scopeId: string, menuKey: string, allow: boolean,
): Promise<MenuCellResult> {
  const { data } = await apiClient.put<MenuCellResult>(cellPath(scopeType, scopeId, menuKey), { allow });
  return { ...data, previous: data?.previous ?? null };
}

/** 칸 하나 지우기 = 상속(개인이면 「부서 기준」)으로 되돌리기. */
export async function clearMenuCell(
  scopeType: MenuScopeType, scopeId: string, menuKey: string,
): Promise<MenuCellResult> {
  const { data } = await apiClient.delete<MenuCellResult>(cellPath(scopeType, scopeId, menuKey));
  return { ...data, previous: data?.previous ?? null };
}

export interface UnassignedMigrateResult {
  ok: boolean;
  /** 옮긴 대상 — 지금 팀이 없는 사람 수. */
  users: number;
  copied: number;
  /** 이미 개인 줄이 있어 건드리지 않은 칸 수. */
  skipped: number;
  /** 지운 묶음 줄 수. */
  cleared: number;
}

/** 남은 「팀없는직원 묶음」 줄 → 지금 팀 없는 사람의 개인 줄로 옮기고 묶음을 지운다 (R7). */
export async function migrateUnassignedRules(): Promise<UnassignedMigrateResult> {
  const { data } = await apiClient.post<UnassignedMigrateResult>('/admin/menu-access/rules/unassigned/all/migrate');
  return data;
}

/* ─────────── 변경 이력 (R5 · 2026-09-15 · 서버 PR #1386) ─────────── */

/** 기록 종류 — 회사 저장 · 전량 교체 · 대상 지우기 · 칸 하나 · 팀없는직원 묶음 옮기기. */
export type MenuHistoryKind = 'company' | 'replace' | 'clear' | 'cell' | 'migrate';
/** 규칙 `inherit | allow | deny` · 회사 `on | off`. 이전 값을 모르면 null. */
export type MenuHistoryValue = 'inherit' | 'allow' | 'deny' | 'on' | 'off';

export interface MenuHistoryChange {
  menuKey: string;
  menuLabel: string;
  before: MenuHistoryValue | null;
  after: MenuHistoryValue | null;
}

export interface MenuHistoryItem {
  id: string;
  /** ISO 시각. */
  at: string;
  /** 지워진 계정이면 null. */
  actor: { id: string; name: string } | null;
  kind: MenuHistoryKind;
  scopeType: string;
  scopeId: string | null;
  scopeName: string | null;
  /** 바뀐 메뉴만. 옮기기 · 옛 지우기 기록은 빈 배열(summary 를 본다). */
  changes: MenuHistoryChange[];
  /** false = 배포 전 기록이라 이전 값이 없다(화면은 「이전 값 없음」). */
  beforeKnown: boolean;
  summary: Record<string, number> | null;
}

/** 이력은 한 대상 기준이다 — 회사는 scopeId 없음 · 팀없는직원은 `all`. */
export interface MenuHistoryScope {
  scopeType: 'company' | 'division' | 'team' | 'user' | 'unassigned';
  scopeId?: string;
}

export interface MenuHistoryPage {
  items: MenuHistoryItem[];
  nextCursor: string | null;
}

/**
 * 한 대상의 변경 이력 — 최신이 위. `nextCursor` 가 있으면 그 값으로 다음 판을 부른다.
 * ⚠ 서버의 감사 기록은 비동기라, 저장 직후 곧바로 부르면 방금 바꾼 것이 몇 ms 늦게 보일 수 있다.
 */
export async function getMenuAccessHistory(
  q: MenuHistoryScope & { menuKey?: string; limit?: number; cursor?: string },
): Promise<MenuHistoryPage> {
  const params: Record<string, string | number> = { scopeType: q.scopeType };
  if (q.scopeType !== 'company' && q.scopeId !== undefined) params.scopeId = q.scopeId;
  if (q.menuKey) params.menuKey = q.menuKey;
  if (q.limit !== undefined) params.limit = q.limit;
  if (q.cursor) params.cursor = q.cursor;
  const { data } = await apiClient.get<MenuHistoryPage>('/admin/menu-access/history', { params });
  const items = Array.isArray(data?.items)
    ? data.items.map((it) => ({
      ...it,
      changes: Array.isArray(it.changes) ? it.changes : [],
      beforeKnown: it.beforeKnown !== false,
      summary: it.summary && typeof it.summary === 'object' ? it.summary : null,
    }))
    : [];
  return { items, nextCursor: typeof data?.nextCursor === 'string' ? data.nextCursor : null };
}
